import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, CaretUp, CaretDown, Trophy, Medal } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const STATUS_LABEL = { actif: "Actif", inactif: "Inactif", suspendu: "Suspendu" };
const STATUS_COLOR = { actif: "bg-emerald-600 hover:bg-emerald-600", inactif: "bg-secondary text-foreground hover:bg-secondary", suspendu: "bg-accent hover:bg-accent" };

const COLUMNS = [
  { key: "student_name", label: "Nom" },
  { key: "filiere", label: "Filière" },
  { key: "promotion", label: "Promotion" },
  { key: "semestre", label: "Semestre" },
  { key: "courses_count", label: "Cours suivis", numeric: true },
  { key: "average", label: "Moyenne", numeric: true },
];

export default function EtudiantsInscrits() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filiereFilter, setFiliereFilter] = useState("all");
  const [promotionFilter, setPromotionFilter] = useState("all");
  const [sortKey, setSortKey] = useState("student_name");
  const [sortDir, setSortDir] = useState("asc");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/grades/overview").then((r) => setStudents(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const filieres = useMemo(() => [...new Set(students.map((s) => s.filiere).filter(Boolean))].sort(), [students]);
  const promotions = useMemo(() => {
    const pool = filiereFilter === "all" ? students : students.filter((s) => s.filiere === filiereFilter);
    return [...new Set(pool.map((s) => s.promotion).filter(Boolean))].sort();
  }, [students, filiereFilter]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "average" || key === "courses_count" ? "desc" : "asc"); }
  };

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = students.filter((s) => {
      if (filiereFilter !== "all" && s.filiere !== filiereFilter) return false;
      if (promotionFilter !== "all" && s.promotion !== promotionFilter) return false;
      if (q && !(`${s.student_name} ${s.email || ""} ${s.username || ""}`.toLowerCase().includes(q))) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === "average") { va = va ?? -1; vb = vb ?? -1; }
      if (va == null) va = "";
      if (vb == null) vb = "";
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return list;
  }, [students, search, filiereFilter, promotionFilter, sortKey, sortDir]);

  const exportPdf = async () => {
    setExporting(true);
    const parts = ["Liste des étudiants"];
    if (filiereFilter !== "all") parts.push(filiereFilter);
    if (promotionFilter !== "all") parts.push(promotionFilter);
    if (sortKey === "average") parts.push("— classement par moyenne");
    try {
      const res = await api.post("/students/roster-pdf", {
        student_ids: filteredSorted.map((s) => s.student_id),
        title: parts.join(" — "),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = "liste-etudiants.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  const rankIcon = (rank) => {
    if (sortKey !== "average" || sortDir !== "desc") return null;
    if (rank === 1) return <Trophy size={14} weight="fill" className="text-amber-500" />;
    if (rank === 2) return <Medal size={14} weight="fill" className="text-slate-400" />;
    if (rank === 3) return <Medal size={14} weight="fill" className="text-amber-700" />;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="etudiants-inscrits-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Étudiants inscrits</h1>
          <p className="text-sm text-muted-foreground">Liste, classement et export de vos étudiants.</p>
        </div>
        <Button onClick={exportPdf} disabled={exporting || filteredSorted.length === 0} className="bg-primary hover:bg-primary/90" data-testid="export-roster-pdf">
          <DownloadSimple size={16} className="mr-2" /> {exporting ? "Génération..." : "Exporter en PDF"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un étudiant..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="roster-search" />
        </div>
        <Select value={filiereFilter} onValueChange={(v) => { setFiliereFilter(v); setPromotionFilter("all"); }}>
          <SelectTrigger className="w-48" data-testid="roster-filiere-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les filières</SelectItem>
            {filieres.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={promotionFilter} onValueChange={setPromotionFilter}>
          <SelectTrigger className="w-48" data-testid="roster-promotion-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les promotions</SelectItem>
            {promotions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!loading && filteredSorted.length === 0 ? (
        <EmptyState icon={MagnifyingGlass} title="Aucun étudiant" description="Aucun résultat pour ces filtres, ou aucun étudiant n'a accès à vos cours." />
      ) : (
        <>
          {/* Vue carte (mobile) */}
          <div className="space-y-2 md:hidden">
            {filteredSorted.map((s, i) => (
              <div key={s.student_id} className="rounded-xl border border-border bg-card p-4" data-testid={`roster-card-${s.student_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1 truncate font-medium">{rankIcon(i + 1)} {s.student_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{s.filiere || "—"} {s.promotion ? `· ${s.promotion}` : ""} {s.semestre ? `· ${s.semestre}` : ""}</p>
                  </div>
                  <span className="shrink-0 font-display text-lg font-bold text-primary">{s.average != null ? `${s.average}/20` : "—"}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Badge className={STATUS_COLOR[s.status || "actif"]}>{STATUS_LABEL[s.status || "actif"]}</Badge>
                  <span className="text-xs text-muted-foreground">{s.courses_count} cours suivi(s)</span>
                </div>
              </div>
            ))}
          </div>

          {/* Vue tableau (desktop) */}
          <div className="hidden rounded-xl border border-border bg-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  {COLUMNS.map((col) => (
                    <TableHead key={col.key} className={col.numeric ? "text-right" : ""}>
                      <button type="button" onClick={() => toggleSort(col.key)} className={`inline-flex items-center gap-1 hover:text-foreground ${col.numeric ? "flex-row-reverse" : ""}`} data-testid={`sort-${col.key}`}>
                        {col.label}
                        {sortKey === col.key && (sortDir === "asc" ? <CaretUp size={12} /> : <CaretDown size={12} />)}
                      </button>
                    </TableHead>
                  ))}
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSorted.map((s, i) => (
                  <TableRow key={s.student_id} data-testid={`roster-row-${s.student_id}`}>
                    <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium"><span className="flex items-center gap-1.5">{rankIcon(i + 1)} {s.student_name}</span></TableCell>
                    <TableCell className="text-muted-foreground">{s.filiere || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.promotion || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{s.semestre || "—"}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{s.courses_count}</TableCell>
                    <TableCell className="text-right font-display font-bold text-primary">{s.average != null ? `${s.average}/20` : "—"}</TableCell>
                    <TableCell><Badge className={STATUS_COLOR[s.status || "actif"]}>{STATUS_LABEL[s.status || "actif"]}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
